/**
 * Dialog de cierre de caja: dos pasos en el mismo modal.
 *
 * Paso 1: input declared_amount + notes opcionales.
 * Paso 2 (post-success): muestra declarado vs esperado vs diferencia, botón salir.
 */

import { Banknote, CheckCircle2, Loader2, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api.ts";
import { formatPEN, toNumber } from "@/lib/utils.ts";
import { useCloseCashSession } from "@/queries/cashSessions.ts";
import { useAuthStore } from "@/stores/auth.ts";
import type { CashSessionOut } from "@/types/api.ts";

interface Props {
  sessionId: string;
  onClose: () => void;
}

export function CloseCashDialog({ sessionId, onClose }: Props) {
  const navigate = useNavigate();
  const clearSession = useAuthStore((s) => s.clearSession);
  const closeSession = useCloseCashSession();

  const [declaredAmount, setDeclaredAmount] = useState("0.00");
  const [notes, setNotes] = useState("");
  const [closed, setClosed] = useState<CashSessionOut | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    closeSession.mutate(
      { session_id: sessionId, declared_amount: declaredAmount, notes: notes || undefined },
      {
        onSuccess: (data) => setClosed(data),
      },
    );
  };

  const handleLogout = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  const errorMsg =
    closeSession.error instanceof ApiError
      ? closeSession.error.message
      : closeSession.error?.message;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
              <Banknote className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              {closed ? "Caja cerrada" : "Cerrar caja"}
            </h3>
          </div>
          {!closed && (
            <button
              type="button"
              onClick={onClose}
              disabled={closeSession.isPending}
              aria-label="Cerrar"
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {!closed ? (
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
            <p className="text-sm text-slate-600">
              Cuenta el efectivo que tenés ahora en caja y declarálo abajo. El sistema lo compara
              contra lo que debería haber según las ventas en efectivo del turno.
            </p>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Monto contado en efectivo (S/)
              </label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={declaredAmount}
                onChange={(e) => setDeclaredAmount(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-2xl font-medium tabular-nums outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15"
                autoFocus
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Notas (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-600"
                placeholder="Retiros, observaciones del turno…"
              />
            </div>

            {errorMsg && (
              <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errorMsg}
              </p>
            )}

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={closeSession.isPending}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={closeSession.isPending || toNumber(declaredAmount) < 0}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:bg-slate-300"
              >
                {closeSession.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {closeSession.isPending ? "Cerrando…" : "Cerrar caja"}
              </button>
            </div>
          </form>
        ) : (
          <CloseSummary closed={closed} onLogout={handleLogout} />
        )}
      </div>
    </div>
  );
}

function CloseSummary({ closed, onLogout }: { closed: CashSessionOut; onLogout: () => void }) {
  const declared = toNumber(closed.declared_amount);
  const expected = toNumber(closed.expected_amount);
  const opened = toNumber(closed.opened_amount);
  const diff = declared - expected;
  const tone =
    Math.abs(diff) < 0.01
      ? { label: "Cuadre exacto", className: "bg-emerald-50 text-emerald-800" }
      : diff > 0
        ? { label: "Sobrante", className: "bg-emerald-50 text-emerald-800" }
        : { label: "Faltante", className: "bg-red-50 text-red-800" };

  return (
    <div className="space-y-4 px-6 py-5">
      <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
        <CheckCircle2 className="h-5 w-5 shrink-0" />
        <span>Sesión cerrada correctamente.</span>
      </div>

      <dl className="space-y-2 rounded-lg border border-slate-200 p-4 text-sm">
        <Row label="Inicial" value={formatPEN(opened)} />
        <Row label="Esperado" value={formatPEN(expected)} muted />
        <Row label="Declarado" value={formatPEN(declared)} bold />
        <div className={`mt-3 flex items-center justify-between rounded-md p-2 ${tone.className}`}>
          <span className="text-xs font-semibold uppercase">{tone.label}</span>
          <span className="text-base font-bold tabular-nums">{formatPEN(diff)}</span>
        </div>
      </dl>

      {closed.notes && (
        <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
          <strong>Notas:</strong> {closed.notes}
        </p>
      )}

      <button
        type="button"
        onClick={onLogout}
        className="w-full rounded-xl bg-brand-600 py-3 text-base font-semibold text-white shadow-sm hover:bg-brand-700"
      >
        Salir
      </button>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className={muted ? "text-slate-500" : "text-slate-700"}>{label}</dt>
      <dd
        className={
          "tabular-nums " +
          (bold ? "font-semibold text-slate-900" : muted ? "text-slate-500" : "text-slate-900")
        }
      >
        {value}
      </dd>
    </div>
  );
}
